import { useEffect, useMemo, useRef, useState } from 'react';
import { navigate } from '../boot/router';
import type { AppUser } from '../boot/session';
import {
  createDrawing,
  deleteDrawing,
  getProject,
  listDrawings,
  listMembers,
  setFavorite,
  upsertMember,
} from '../data/repo';
import { listVersions } from '../data/versionRepo';
import { listBaseDrawings, type BaseDrawingRow } from '../data/baseDrawingRepo';
import { saveDocument } from '../data/drawingRepo';
import { sb } from '../data/supabase';
import {
  createUnderlayFromPdf,
  enqueueImport,
  readDwgVersion,
  uploadSource,
  validateUpload,
  watchJob,
  type FileJob,
} from '../data/uploadRepo';
import {
  MEMBER_ROLE_LABEL,
  PROJECT_STATUS_LABEL,
  PROJECT_TYPE_LABEL,
  type DrawingRow,
  type MemberRole,
} from '../data/types';
import { useAction, useAsync } from '../hooks/useAsync';
import { useUserDirectory } from '../hooks/useUserDirectory';
import { Modal, ModalCancel } from '../shell/Modal';
import { OneUI } from '../shell/oneUi';
import { EmptyState, ErrorState, LoadingState } from '../shell/State';
import { FavoriteStar, relTime, VersionStatusBadge } from '../shell/bits';
import { parseDxfFile } from '../cad/format/dxfAdapter';
import { parsePdfFile } from '../cad/format/pdfAdapter';
import { emptyDocument } from '../cad/model/document';
import { PdfScalePicker, WARNING_LABEL } from '../cad/ui/FileMenu';
import type { ConversionWarning } from '../cad/format/adapter';

type Tab = 'drawings' | 'members';

/** 프로젝트 상세 — 도면 목록 · 멤버 (작업지시서 P1-04 / P1-05) */
export function ProjectDetailPage({ projectId, user }: { projectId: string; user: AppUser }) {
  const [tab, setTab] = useState<Tab>('drawings');
  const project = useAsync(() => getProject(projectId), [projectId]);

  if (project.loading) return <LoadingState label="프로젝트를 불러오는 중…" />;
  if (project.error)
    return (
      <div className="page">
        <ErrorState message={project.error} onRetry={project.reload} />
      </div>
    );
  if (!project.data)
    return (
      <div className="page">
        <div className="card">
          <EmptyState
            icon="🔒"
            title="프로젝트를 찾을 수 없습니다"
            desc="삭제되었거나 접근 권한이 없습니다."
            action={
              <button
                type="button"
                className="btn"
                style={{ marginTop: 10 }}
                onClick={() => navigate({ name: 'projects' })}
              >
                프로젝트 목록으로
              </button>
            }
          />
        </div>
      </div>
    );

  const p = project.data;
  // 프로젝트 단위 권한. 시스템 역할과 별개다.
  const canEditHere = p.my_role === 'owner' || p.my_role === 'designer';
  const canManage = p.my_role === 'owner';

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <button
            type="button"
            className="section__more"
            style={{ padding: 0, marginBottom: 4 }}
            onClick={() => navigate({ name: 'projects' })}
          >
            ← 프로젝트 목록
          </button>
          <h1 className="page__title">{p.name}</h1>
          <p className="page__desc">
            <span className="badge badge--type">{PROJECT_TYPE_LABEL[p.project_type]}</span>{' '}
            {p.store_name && <>{p.store_name} </>}
            {p.store_code && <>({p.store_code}) </>}
            · {PROJECT_STATUS_LABEL[p.status]}
            {p.my_role && <> · 내 역할: {MEMBER_ROLE_LABEL[p.my_role]}</>}
          </p>
        </div>
      </header>

      {!canEditHere && (
        <div className="notice">
          <span aria-hidden="true">👁</span>
          <span>
            열람 권한으로 접속했습니다. 도면 편집과 생성은 설계자 이상만 가능합니다.
          </span>
        </div>
      )}

      <div className="toolbar" role="tablist" aria-label="프로젝트 탭">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'drawings'}
          className={tab === 'drawings' ? 'btn btn--primary btn--sm' : 'btn btn--sm'}
          onClick={() => setTab('drawings')}
        >
          도면 {p.drawing_count}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'members'}
          className={tab === 'members' ? 'btn btn--primary btn--sm' : 'btn btn--sm'}
          onClick={() => setTab('members')}
        >
          멤버
        </button>
      </div>

      {tab === 'drawings' && (
        <DrawingsTab
          projectId={projectId}
          canEdit={canEditHere}
          canManage={canManage}
          myEmail={user.email}
        />
      )}
      {tab === 'members' && <MembersTab projectId={projectId} canManage={canManage} />}
    </div>
  );
}

/* ---------------------------------------------------------------- 도면 탭 */

function DrawingsTab({
  projectId,
  canEdit,
  canManage,
  myEmail,
}: {
  projectId: string;
  canEdit: boolean;
  canManage: boolean;
  myEmail: string;
}) {
  const [query, setQuery] = useState('');
  const [favOnly, setFavOnly] = useState(false);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [fromBase, setFromBase] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const state = useAsync(() => listDrawings(projectId), [projectId]);

  /**
   * 지울 수 있는가. RLS(0011)와 같은 규칙을 화면에서도 보여준다 —
   * 버튼을 눌러 보고 나서야 거절당하면 사용자는 이유를 모른다.
   */
  function deletable(d: DrawingRow): { ok: boolean; why?: string } {
    if (d.published_version_id) {
      return { ok: false, why: '현장 배포본은 지울 수 없습니다. 먼저 배포를 내리세요.' };
    }
    if (canManage) return { ok: true };
    if (d.created_by === myEmail) return { ok: true };
    return { ok: false, why: '내가 만든 도면만 지울 수 있습니다.' };
  }

  async function removeDrawing(d: DrawingRow) {
    const ok = await OneUI.confirm(
      '이 도면의 모든 버전·원본 파일·댓글이 함께 사라지며 되돌릴 수 없습니다.',
      { title: `"${d.name}" 을(를) 삭제할까요?`, okText: '삭제', danger: true }
    );
    if (!ok) return;

    setDeletingId(d.id);
    setDeleteError(null);
    try {
      await deleteDrawing(d.id, d.name);
      state.setData((state.data ?? []).filter((x) => x.id !== d.id));
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeletingId(null);
    }
  }

  const rows = useMemo(() => {
    const list = state.data ?? [];
    const q = query.trim().toLowerCase();
    return list.filter((d) => {
      if (favOnly && !d.is_favorite) return false;
      if (!q) return true;
      return (
        d.name.toLowerCase().includes(q) ||
        (d.floor_name ?? '').toLowerCase().includes(q) ||
        d.zone_tags.some((t) => t.toLowerCase().includes(q))
      );
    });
  }, [state.data, query, favOnly]);

  async function toggleFav(id: string, next: boolean) {
    // 낙관적 갱신 — 실패 시 되돌린다.
    const before = state.data ?? [];
    state.setData(before.map((d) => (d.id === id ? { ...d, is_favorite: next } : d)));
    try {
      await setFavorite(id, next);
    } catch {
      state.setData(before);
    }
  }

  return (
    <>
      <div className="toolbar">
        <input
          className="input input--search"
          type="search"
          placeholder="도면명 · 층 · 구역 검색"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="도면 검색"
        />
        <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={favOnly} onChange={(e) => setFavOnly(e.target.checked)} />
          즐겨찾기만
        </label>
        <div className="toolbar__spacer" />
        {canEdit && (
          <>
            <button
              type="button"
              className="btn"
              onClick={() => setFromBase(true)}
              title="관리자가 등록해 둔 매장 현황 도면을 복사해 시작합니다"
            >
              + 기본 도면에서 만들기
            </button>
            <button type="button" className="btn" onClick={() => setImporting(true)}>
              + 파일에서 만들기
            </button>
            <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>
              + 새 도면
            </button>
          </>
        )}
      </div>

      {deleteError && (
        <div className="form-error" role="alert">
          {deleteError}{' '}
          <button type="button" className="section__more" onClick={() => setDeleteError(null)}>
            닫기
          </button>
        </div>
      )}

      {state.loading && <LoadingState label="도면을 불러오는 중…" />}
      {!state.loading && state.error && <ErrorState message={state.error} onRetry={state.reload} />}

      {!state.loading && !state.error && rows.length === 0 && (
        <div className="card">
          <EmptyState
            icon="⬚"
            title={query || favOnly ? '조건에 맞는 도면이 없습니다' : '아직 도면이 없습니다'}
            desc={
              query || favOnly
                ? '검색어나 필터를 바꿔 보세요.'
                : 'DWG · DXF · DWT 원본을 올리거나 빈 도면부터 시작할 수 있습니다.'
            }
          />
        </div>
      )}

      {!state.loading && !state.error && rows.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <span className="sr-only">즐겨찾기</span>
                </th>
                <th>도면명</th>
                <th>층</th>
                <th>구역</th>
                <th>최신 버전</th>
                <th>상태</th>
                <th className="table__num">객체 수</th>
                <th>수정</th>
                {canEdit && <th aria-label="삭제"></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr
                  key={d.id}
                  className="row-link"
                  onClick={() => navigate({ name: 'drawing', drawingId: d.id })}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <FavoriteStar
                      on={d.is_favorite}
                      label={d.name}
                      onToggle={() => void toggleFav(d.id, !d.is_favorite)}
                    />
                  </td>
                  <td className="row-link__name">{d.name}</td>
                  <td>{d.floor_name ?? '—'}</td>
                  <td>
                    {d.zone_tags.length === 0
                      ? '—'
                      : d.zone_tags.map((t) => (
                          <span className="tag" key={t} style={{ marginRight: 4 }}>
                            {t}
                          </span>
                        ))}
                  </td>
                  <td>{d.latest_version ? `v${d.latest_version.version_number}` : '—'}</td>
                  <td>
                    {d.latest_version ? (
                      <VersionStatusBadge status={d.latest_version.status} />
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="table__num">
                    {d.latest_version ? d.latest_version.entity_count.toLocaleString('ko-KR') : '—'}
                  </td>
                  <td>{relTime(d.updated_at)}</td>
                  {canEdit && (
                    <td onClick={(e) => e.stopPropagation()}>
                      {(() => {
                        const can = deletable(d);
                        return (
                          <button
                            type="button"
                            className="btn btn--sm btn--danger"
                            disabled={!can.ok || deletingId !== null}
                            title={can.why ?? '이 도면을 삭제합니다'}
                            onClick={() => void removeDrawing(d)}
                          >
                            {deletingId === d.id ? '삭제 중…' : '삭제'}
                          </button>
                        );
                      })()}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <NewDrawingModal
          projectId={projectId}
          onClose={() => setCreating(false)}
          onCreated={(id) => navigate({ name: 'drawing', drawingId: id })}
        />
      )}

      {importing && (
        <ImportDrawingModal
          projectId={projectId}
          onClose={() => setImporting(false)}
          onImported={(id) => navigate({ name: 'drawing', drawingId: id })}
        />
      )}

      {fromBase && (
        <FromBaseDrawingModal
          projectId={projectId}
          onClose={() => setFromBase(false)}
          onCreated={(id) => navigate({ name: 'drawing', drawingId: id })}
        />
      )}
    </>
  );
}

/* ---------------------------------------------------------- 파일에서 만들기 */

type ImportPhase =
  | 'idle'
  | 'creating'
  | 'uploading'
  | 'converting'
  | 'saving'
  | 'done'
  | 'failed';

const PHASE_LABEL: Partial<Record<ImportPhase, string>> = {
  creating: '도면 만드는 중…',
  uploading: '원본 업로드 중…',
  converting: '변환 중…',
  saving: '결과 저장 중…',
};

function isDwgOrDwt(f: File | null): boolean {
  return !!f && /\.(dwg|dwt)$/i.test(f.name);
}

function isPdf(f: File | null): boolean {
  return !!f && /\.pdf$/i.test(f.name);
}

/**
 * DWG/DXF/DWT 원본을 올려 새 도면으로 등록한다 (작업지시서 P3-04·P3-06·P3-07).
 *
 * ★ DWG/DWT 는 브라우저에서 못 읽는다. 업로드 → 큐 등록 → 워커가 변환 →
 *   결과(DXF)를 다시 받아와 파싱하는 비동기 왕복이다. 그래서 "새 도면" 처럼
 *   한 번에 안 끝나고, 단계별 진행 상태를 보여줘야 한다 — 안 그러면 사용자는
 *   몇 분짜리 변환을 "먹통" 으로 오해한다.
 */
function ImportDrawingModal({
  projectId,
  onClose,
  onImported,
}: {
  projectId: string;
  onClose: () => void;
  onImported: (drawingId: string) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [floor, setFloor] = useState('');
  const [phase, setPhase] = useState<ImportPhase>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [versionNote, setVersionNote] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<ConversionWarning[]>([]);
  const [pdfMode, setPdfMode] = useState<'import' | 'attach'>('import');
  const [pdfScaleDenom, setPdfScaleDenom] = useState(100);
  const unsubRef = useRef<(() => void) | null>(null);

  // 모달이 닫히거나 언마운트될 때 진행 중인 구독을 끊는다 — 안 그러면
  // 창을 닫은 뒤에도 백그라운드에서 계속 폴링·상태갱신이 돈다.
  useEffect(() => () => unsubRef.current?.(), []);

  const busy = phase !== 'idle' && phase !== 'failed' && phase !== 'done';

  function pickFile(f: File | null) {
    setError(null);
    setVersionNote(null);
    if (!f) {
      setFile(null);
      return;
    }
    const check = validateUpload(f);
    if (!check.ok) {
      setError(check.reason ?? '이 파일은 올릴 수 없습니다.');
      setFile(null);
      return;
    }
    setFile(f);
    if (!name.trim()) setName(f.name.replace(/\.[^.]+$/, ''));

    // 올리기 전에 DWG 버전을 미리 본다 — 변환에 몇 분 기다린 뒤에
    // "화면이 비었다" 를 알게 되면 사용자는 먹통으로 받아들인다.
    void readDwgVersion(f).then((v) => {
      if (v?.risky) {
        setVersionNote(
          `이 파일은 AutoCAD ${v.label} 형식입니다. 변환 엔진이 이 형식을 완전히 읽지 못해 ` +
            `도형이 유실될 수 있습니다. 확실하게 하려면 AutoCAD 에서 ` +
            `"다른 이름으로 저장 → AutoCAD 2000/LT2000 DXF" 로 저장한 뒤 그 DXF 를 올리세요. ` +
            `그대로 진행해도 됩니다 — 결과가 비면 알려 드립니다.`
        );
      }
    });
  }

  /** 변환 결과(cad-derived 의 DXF)를 받아 도면 버전에 반영한다 */
  async function applyResult(job: FileJob, versionId: string): Promise<void> {
    setPhase('saving');

    const derivedPath = (job.result as { derivedPath?: string }).derivedPath;
    if (!derivedPath) throw new Error('변환 결과 파일 경로를 찾을 수 없습니다.');

    const { data: blob, error: dlErr } = await sb()
      .storage.from('cad-derived')
      .download(derivedPath);
    if (dlErr || !blob) throw new Error(`변환 결과를 받아오지 못했습니다: ${dlErr?.message ?? ''}`);

    const { doc, warnings: parseWarnings } = await parseDxfFile(blob);
    // 새로 만든 빈 버전이라 연산 이력 없이 스냅샷만 채운다 — 최초 등록은 "편집" 이 아니다.
    await saveDocument(versionId, doc, []);

    setWarnings([...job.warnings, ...parseWarnings]);
  }

  /** 작업을 큐에 넣고, 끝날 때까지 지켜본다 */
  function runConversion(drawingId: string, versionId: string, sourceId: string): Promise<void> {
    return enqueueImport({ drawingId, sourceId }).then(
      (jobId) =>
        new Promise<void>((resolve, reject) => {
          setPhase('converting');
          const unsub = watchJob(jobId, (job) => {
            setProgress(job.progress);

            if (job.status === 'completed' || job.status === 'completed_with_warnings') {
              unsub();
              applyResult(job, versionId).then(resolve, reject);
              return;
            }
            if (job.status === 'failed') {
              unsub();
              reject(new Error(job.error_message ?? '변환에 실패했습니다.'));
            }
          });
          unsubRef.current = unsub;
        })
    );
  }

  /** PDF 는 워커 큐를 타지 않는다 — DXF 처럼 브라우저에서 바로 처리한다. */
  async function submitPdf(f: File) {
    setPhase('creating');
    const drawing = await createDrawing({
      project_id: projectId,
      name: name.trim() || f.name.replace(/\.[^.]+$/, ''),
      floor_name: floor || null,
    });

    const versions = await listVersions(drawing.id);
    const versionId = versions[0]?.id;
    if (!versionId) throw new Error('방금 만든 버전을 찾을 수 없습니다.');

    setPhase('converting');
    if (pdfMode === 'import') {
      const { doc, warnings: w } = await parsePdfFile(f, { scaleDenom: pdfScaleDenom });
      setPhase('saving');
      await saveDocument(versionId, doc, []);
      setWarnings(w);
    } else {
      const underlay = await createUnderlayFromPdf({
        projectId,
        drawingId: drawing.id,
        versionId,
        file: f,
        scaleDenom: pdfScaleDenom,
        center: { x: 0, y: 0 },
      });
      setPhase('saving');
      const doc = emptyDocument('mm');
      doc.underlays = [underlay];
      await saveDocument(versionId, doc, []);
      setWarnings([]);
    }

    setPhase('done');
    window.setTimeout(() => onImported(drawing.id), 700);
  }

  async function submit() {
    if (!file) return;
    setError(null);

    try {
      if (isPdf(file)) {
        await submitPdf(file);
        return;
      }

      setPhase('creating');
      const drawing = await createDrawing({
        project_id: projectId,
        name: name.trim() || file.name.replace(/\.[^.]+$/, ''),
        floor_name: floor || null,
      });

      // 방금 만든 도면의 초기 버전(v1) — 여기에 변환 결과를 채운다.
      const versions = await listVersions(drawing.id);
      const versionId = versions[0]?.id;
      if (!versionId) throw new Error('방금 만든 버전을 찾을 수 없습니다.');

      setPhase('uploading');
      const uploaded = await uploadSource({
        projectId,
        drawingId: drawing.id,
        versionId,
        file,
      });

      await runConversion(drawing.id, versionId, uploaded.id);

      setPhase('done');
      window.setTimeout(() => onImported(drawing.id), 700);
    } catch (e) {
      setPhase('failed');
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <Modal
      title="파일에서 도면 만들기"
      desc="DWG · DXF · DWT · PDF 원본을 올리면 변환 후 새 도면으로 등록됩니다. 원본은 그대로 보관됩니다."
      onClose={onClose}
      // 다 가져온 뒤에는 닫아도 잃을 것이 없다
      isDirty={() => phase !== 'done' && (!!file || !!name.trim() || !!floor.trim())}
      footer={
        <>
          <ModalCancel disabled={busy}>{phase === 'done' ? '닫기' : '취소'}</ModalCancel>
          {phase !== 'done' && (
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void submit()}
              disabled={!file || busy}
            >
              {busy ? '진행 중…' : '가져오기'}
            </button>
          )}
        </>
      }
    >
      {error && (
        <div className="form-error">
          {error}
          {phase === 'failed' && isDwgOrDwt(file) && (
            <p style={{ marginTop: 8 }}>
              DWG 변환이 실패했다면, 아래 링크의 온라인 변환기에서 이 파일을 DXF 로 바꾼 뒤
              그 DXF 파일로 다시 올려 보세요.
            </p>
          )}
          {phase === 'failed' && (
            <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => {
                  setError(null);
                  setPhase('idle');
                }}
              >
                다시 시도
              </button>
              {isDwgOrDwt(file) && (
                <button
                  type="button"
                  className="btn btn--sm"
                  onClick={() =>
                    window.open(
                      'https://www.coolutils.com/ko/online/DWG-to-DXF',
                      '_blank',
                      'noopener,noreferrer'
                    )
                  }
                >
                  DXF로 변환하기 (새 창)
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {(phase === 'idle' || phase === 'failed') && (
        <>
          <div className="field">
            <label className="field__label" htmlFor="im-file">
              파일 *
            </label>
            <input
              id="im-file"
              className="input"
              type="file"
              accept=".dwg,.dxf,.dwt,.pdf"
              onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
            />
            <span className="field__hint">DWG · DXF · DWT · PDF, 최대 500MB</span>
          </div>

          {versionNote && (
            <p className="banner banner--warn" role="note">
              {versionNote}
            </p>
          )}

          {isPdf(file) && (
            <>
              <div className="field">
                <span className="field__label">PDF 처리 방식</span>
                <div style={{ display: 'flex', gap: 14 }}>
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                    <input
                      type="radio"
                      name="pdf-mode"
                      checked={pdfMode === 'import'}
                      onChange={() => setPdfMode('import')}
                    />
                    편집 가능하게 가져오기 (PDFIMPORT)
                  </label>
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                    <input
                      type="radio"
                      name="pdf-mode"
                      checked={pdfMode === 'attach'}
                      onChange={() => setPdfMode('attach')}
                    />
                    부착만(참조용, 편집 불가)
                  </label>
                </div>
                <span className="field__hint">
                  {pdfMode === 'import'
                    ? 'PDF 안의 선·텍스트를 편집 가능한 CAD 객체로 바꿉니다. 스캔본(래스터)에는 쓸 수 없습니다.'
                    : 'PDF 한 페이지를 배경 그림으로만 붙입니다. 스캔본도 됩니다.'}
                </span>
              </div>
              <PdfScalePicker
                id="im-pdf-scale"
                scaleDenom={pdfScaleDenom}
                onChange={setPdfScaleDenom}
              />
            </>
          )}

          <div className="field">
            <label className="field__label" htmlFor="im-name">
              도면명
            </label>
            <input
              id="im-name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="비워두면 파일명을 씁니다"
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="im-floor">
              층
            </label>
            <input
              id="im-floor"
              className="input"
              value={floor}
              onChange={(e) => setFloor(e.target.value)}
              placeholder="1F"
            />
          </div>
        </>
      )}

      {busy && (
        <div className="field">
          <p className="field__hint">{PHASE_LABEL[phase]}</p>
          {phase === 'converting' && (
            <div className="progress">
              <div className="progress__bar" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>
      )}

      {phase === 'done' && (
        <>
          <p style={{ color: 'var(--sc-ok, #1a9c6e)', fontWeight: 600 }}>가져오기 완료</p>
          {warnings.length > 0 && (
            <ul className="warn-list">
              {warnings.map((w, i) => (
                <li key={`${w.code}-${w.subject ?? i}`} className="warn-list__item">
                  <span className="warn-list__badge">{WARNING_LABEL[w.code] ?? w.code}</span>
                  <span>
                    {w.message}
                    {w.count && w.count > 1 && <b> ({w.count}건)</b>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Modal>
  );
}

function NewDrawingModal({
  projectId,
  onClose,
  onCreated,
}: {
  projectId: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = useState('');
  const [floor, setFloor] = useState('');
  const { run, busy, error } = useAction();
  const valid = name.trim().length > 0;

  async function submit() {
    let id = '';
    const ok = await run(async () => {
      const d = await createDrawing({ project_id: projectId, name, floor_name: floor || null });
      id = d.id;
    });
    if (ok) onCreated(id);
  }

  return (
    <Modal
      title="새 도면"
      desc="빈 도면을 만들고 main 브랜치와 초안 버전(v1)이 함께 생성됩니다."
      onClose={onClose}
      footer={
        <>
          <ModalCancel disabled={busy}>취소</ModalCancel>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void submit()}
            disabled={!valid || busy}
          >
            {busy ? '만드는 중…' : '만들기'}
          </button>
        </>
      }
    >
      {error && <div className="form-error">{error}</div>}
      <div className="field">
        <label className="field__label" htmlFor="nd-name">
          도면명 *
        </label>
        <input
          id="nd-name"
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="예: 1F 리뉴얼 A안"
        />
      </div>
      <div className="field">
        <label className="field__label" htmlFor="nd-floor">
          층
        </label>
        <input
          id="nd-floor"
          className="input"
          value={floor}
          onChange={(e) => setFloor(e.target.value)}
          placeholder="1F"
        />
      </div>
    </Modal>
  );
}

/**
 * 기본 도면에서 시작하기.
 *
 * CAD 관리자가 등록해 둔 매장 현황(cad_base_drawing)을 골라 **복사본**으로
 * 새 도면을 만든다. 매번 벽체부터 다시 그리는 낭비를 없애는 것이 목적이다.
 *
 * ★ 복사지 참조가 아니다 — 나중에 관리자가 기본 도면을 갱신해도 여기서 만든
 *   도면은 그대로 남는다. 검토 중이던 안이 남의 손에 바뀌면 안 되기 때문이다.
 */
function FromBaseDrawingModal({
  projectId,
  onClose,
  onCreated,
}: {
  projectId: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [bases, setBases] = useState<BaseDrawingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [storeFilter, setStoreFilter] = useState('');
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [floor, setFloor] = useState('');
  const { run, busy, error } = useAction();

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const rows = await listBaseDrawings();
        if (!alive) return;
        setBases(rows);
      } catch (e) {
        if (alive) setLoadError(e instanceof Error ? e.message : String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // 기본도면이 등록된 점포만 — 등록 안 된 점포까지 목록에 나오면 골라도 항상 비어 있다
  const stores = useMemo(() => {
    const set = new Set<string>();
    for (const b of bases) if (b.storeCode) set.add(b.storeCode);
    return [...set].sort((a, b) => a.localeCompare(b, 'ko'));
  }, [bases]);

  const visibleBases = useMemo(
    () => (storeFilter ? bases.filter((b) => b.storeCode === storeFilter) : bases),
    [bases, storeFilter]
  );

  const picked = bases.find((b) => b.id === pickedId) ?? null;

  function choose(b: BaseDrawingRow) {
    setPickedId(b.id);
    // 이름을 비워 두면 뭘 만든 건지 목록에서 구분이 안 된다 — 기본값을 채워 준다
    if (!name.trim()) setName(`${b.name} 사본`);
    if (!floor.trim() && b.floorName) setFloor(b.floorName);
  }

  async function submit() {
    if (!picked) return;
    let id = '';
    const ok = await run(async () => {
      // 본문은 서버가 DB 안에서 복사한다 — id 만 넘긴다.
      // 예전에는 여기서 기본 도면 전체를 내려받아 다시 올렸는데, 큰 매장 도면은
      // 그 왕복만으로 8초 제한을 넘겨 '초기 버전 생성: canceling statement due to
      // statement timeout' 으로 끊겼다(그리고 껍데기 도면이 남았다).
      const d = await createDrawing({
        project_id: projectId,
        name,
        floor_name: floor || null,
        baseDrawingId: picked.id,
        baseDrawingName: picked.name,
      });
      id = d.id;
    });
    if (ok) onCreated(id);
  }

  return (
    <Modal
      title="기본 도면에서 만들기"
      desc="관리자가 등록한 매장 현황을 복사해 새 도면을 만듭니다. 원본은 바뀌지 않습니다."
      onClose={onClose}
      // 목록이 늦게 그려져 입력칸 값으로는 못 견준다 — 고른 것·적은 것으로 본다
      isDirty={() => !!pickedId || !!name.trim() || !!floor.trim()}
      footer={
        <>
          <ModalCancel disabled={busy}>취소</ModalCancel>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void submit()}
            disabled={!picked || !name.trim() || busy}
          >
            {busy ? '만드는 중…' : '만들기'}
          </button>
        </>
      }
    >
      {error && <div className="form-error">{error}</div>}
      {loadError && <div className="form-error">{loadError}</div>}

      {loading && <LoadingState label="기본 도면을 불러오는 중…" />}

      {!loading && !loadError && bases.length === 0 && (
        <EmptyState
          icon="⬚"
          title="등록된 기본 도면이 없습니다"
          desc="CAD 관리자가 설정 → 도면 관리에서 먼저 등록해야 합니다."
        />
      )}

      {!loading && bases.length > 0 && (
        <>
          {stores.length > 0 && (
            <div className="field">
              <label className="field__label" htmlFor="fb-store">
                점포 선택
              </label>
              <select
                id="fb-store"
                className="select"
                value={storeFilter}
                onChange={(e) => {
                  setStoreFilter(e.target.value);
                  setPickedId(null);
                }}
              >
                <option value="">전체 점포</option>
                {stores.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <span className="field__hint">
                기본 도면이 등록된 점포만 나옵니다. 고르면 아래 목록이 그 점포로 좁혀집니다.
              </span>
            </div>
          )}

          <div className="field">
            <label className="field__label" htmlFor="fb-pick">
              기본 도면 고르기 *
            </label>
            <select
              id="fb-pick"
              className="select"
              value={pickedId ?? ''}
              disabled={visibleBases.length === 0}
              onChange={(e) => {
                const b = visibleBases.find((x) => x.id === e.target.value);
                if (b) choose(b);
              }}
            >
              <option value="" disabled>
                {visibleBases.length === 0
                  ? '이 점포에 등록된 기본 도면이 없습니다'
                  : '— 등록된 기본 도면 중에서 고르세요 —'}
              </option>
              {visibleBases.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                  {[b.storeCode, b.floorName].filter(Boolean).length > 0
                    ? ` (${[b.storeCode, b.floorName].filter(Boolean).join(' · ')})`
                    : ''}
                </option>
              ))}
            </select>
            {picked?.description && (
              <span className="field__hint">{picked.description}</span>
            )}
          </div>

          <div className="field">
            <label className="field__label" htmlFor="fb-name">
              새 도면명 *
            </label>
            <input
              id="fb-name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="예: 1F 리뉴얼 A안"
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="fb-floor">
              층
            </label>
            <input
              id="fb-floor"
              className="input"
              value={floor}
              onChange={(e) => setFloor(e.target.value)}
              placeholder="1F"
            />
          </div>
        </>
      )}
    </Modal>
  );
}

/* ---------------------------------------------------------------- 멤버 탭 */

function MembersTab({ projectId, canManage }: { projectId: string; canManage: boolean }) {
  const state = useAsync(() => listMembers(projectId), [projectId]);
  const [inviting, setInviting] = useState(false);
  const { nameOf } = useUserDirectory();

  const existingEmails = useMemo(
    () => new Set((state.data ?? []).map((m) => m.email)),
    [state.data]
  );

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__spacer" />
        {canManage && (
          <button type="button" className="btn btn--primary" onClick={() => setInviting(true)}>
            + 멤버 추가
          </button>
        )}
      </div>

      {state.loading && <LoadingState />}
      {!state.loading && state.error && <ErrorState message={state.error} onRetry={state.reload} />}

      {!state.loading && !state.error && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>이름</th>
                <th>프로젝트 역할</th>
                <th>추가한 사람</th>
                <th>추가일</th>
              </tr>
            </thead>
            <tbody>
              {(state.data ?? []).map((m) => (
                <tr key={m.email}>
                  <td className="row-link__name" title={m.email}>
                    {nameOf(m.email)}
                  </td>
                  <td>{MEMBER_ROLE_LABEL[m.role]}</td>
                  <td>{m.invited_by ? nameOf(m.invited_by) : '—'}</td>
                  <td>{relTime(m.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {inviting && (
        <InviteMemberModal
          projectId={projectId}
          existingEmails={existingEmails}
          onClose={() => setInviting(false)}
          onDone={() => {
            setInviting(false);
            state.reload();
          }}
        />
      )}
    </>
  );
}

/**
 * 멤버 추가 — 이름·이메일 검색 + 다중 선택.
 *
 * ★ 자유 텍스트 이메일 입력을 없앤 이유 — 오타로 엉뚱한 주소를 추가하기
 *   쉽고, FK 제약(ONE 플랫폼에 들어온 적 있는 계정만) 때문에 실패해도
 *   "왜 안 되는지" 를 이메일 문자열만으로는 알기 어렵다. 검색 결과에
 *   뜬다는 것 자체가 "추가 가능한 계정" 이라는 확인이 된다.
 */
function InviteMemberModal({
  projectId,
  existingEmails,
  onClose,
  onDone,
}: {
  projectId: string;
  existingEmails: Set<string>;
  onClose: () => void;
  onDone: () => void;
}) {
  const { activeEntries, loading: dirLoading } = useUserDirectory();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [role, setRole] = useState<MemberRole>('viewer');
  const { run, busy, error } = useAction();

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return activeEntries
      .filter((e) => !existingEmails.has(e.email))
      .filter((e) => e.name.toLowerCase().includes(q) || e.email.toLowerCase().includes(q))
      .slice(0, 20);
  }, [activeEntries, query, existingEmails]);

  function toggle(email: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  }

  async function submit() {
    const emails = [...selected];
    if (emails.length === 0) return;
    const ok = await run(async () => {
      for (const email of emails) {
        await upsertMember(projectId, email, role);
      }
    });
    if (ok) onDone();
  }

  return (
    <Modal
      title="멤버 추가"
      desc="이름 또는 이메일로 검색해서 여러 명을 한 번에 추가할 수 있습니다."
      onClose={onClose}
      // 검색어는 내용이 아니다 — 고른 사람이 있을 때만 묻는다
      isDirty={() => selected.size > 0}
      footer={
        <>
          <ModalCancel disabled={busy}>취소</ModalCancel>
          <button
            type="button"
            className="btn btn--primary"
            disabled={selected.size === 0 || busy}
            onClick={() => void submit()}
          >
            {busy ? '추가 중…' : `추가 (${selected.size})`}
          </button>
        </>
      }
    >
      {error && <div className="form-error">{error}</div>}

      <div className="field">
        <label className="field__label" htmlFor="im-search">
          이름 또는 이메일로 검색 *
        </label>
        <input
          id="im-search"
          className="input"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="홍길동 또는 name@trial-net.co.kr"
          autoFocus
        />
        <span className="field__hint">
          {dirLoading
            ? '사용자 목록을 불러오는 중…'
            : 'StoreCAD 에 한 번도 들어온 적 없는 계정은 검색되지 않습니다. 먼저 접속을 요청하세요.'}
        </span>
      </div>

      {query.trim() && (
        <ul className="member-picker__list">
          {matches.length === 0 && <li className="cad-panel__hint">일치하는 사람이 없습니다.</li>}
          {matches.map((m) => (
            <li key={m.email}>
              <label className="member-picker__row">
                <input
                  type="checkbox"
                  checked={selected.has(m.email)}
                  onChange={() => toggle(m.email)}
                />
                <span>
                  <b>{m.name}</b>{' '}
                  <span style={{ color: 'var(--one-text-muted)', fontSize: 12 }}>{m.email}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}

      {selected.size > 0 && (
        <div className="field">
          <span className="field__label">선택됨 ({selected.size})</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {[...selected].map((email) => {
              const m = activeEntries.find((e) => e.email === email);
              return (
                <span key={email} className="badge badge--type member-picker__chip">
                  {m?.name ?? email}
                  <button
                    type="button"
                    onClick={() => toggle(email)}
                    aria-label={`${m?.name ?? email} 선택 해제`}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}

      <div className="field">
        <label className="field__label" htmlFor="im-role">
          프로젝트 역할
        </label>
        <select
          id="im-role"
          className="select"
          value={role}
          onChange={(e) => setRole(e.target.value as MemberRole)}
        >
          {(Object.keys(MEMBER_ROLE_LABEL) as MemberRole[]).map((r) => (
            <option key={r} value={r}>
              {MEMBER_ROLE_LABEL[r]}
            </option>
          ))}
        </select>
        <span className="field__hint">선택한 모든 사람에게 같은 역할이 적용됩니다.</span>
      </div>
    </Modal>
  );
}
